import apiClient from './client';

// ==================== Types ====================

export interface MfaStatus {
  isEnabled: boolean;
  enabledAt: string | null;
  method: string;
  backupCodesRemaining: number;
  hasRecoveryEmail: boolean;
  isLockedOut: boolean;
}

export interface MfaSetupResponse {
  secretKey: string;
  qrCodeUri: string;
  manualEntryKey: string;
}

export interface MfaEnableResponse {
  success: boolean;
  errorMessage?: string;
  backupCodes?: string[];
}

export interface MfaVerifyResponse {
  success: boolean;
  errorMessage?: string;
  isLockedOut: boolean;
  lockedUntil?: string;
}

export interface MfaBackupCodesResponse {
  success: boolean;
  backupCodes?: string[];
}

export interface MfaRequiredResponse {
  requiresMfa: boolean;
  mfaSessionToken: string;
  email: string;
  expiresAt: string;
}

// ==================== Request Types ====================

export interface MfaVerifyRequest {
  code: string;
}

export interface MfaBackupCodeRequest {
  backupCode: string;
}

export interface MfaDisableRequest {
  code: string;
}

export interface MfaRegenerateBackupCodesRequest {
  code: string;
}

export interface MfaSetRecoveryEmailRequest {
  recoveryEmail: string;
  code: string;
}

export interface MfaLoginRequest {
  mfaSessionToken: string;
  code: string;
  isBackupCode: boolean;
}

// ==================== API Functions ====================

/**
 * Get current MFA status for the authenticated user
 */
export async function getMfaStatus(): Promise<MfaStatus> {
  const response = await apiClient.get<MfaStatus>('/mfa/status');
  return response.data;
}

/**
 * Initiate MFA setup - generates secret key and QR code URI
 */
export async function initiateMfaSetup(): Promise<MfaSetupResponse> {
  const response = await apiClient.post<MfaSetupResponse>('/mfa/setup');
  return response.data;
}

/**
 * Complete MFA setup by verifying a TOTP code
 */
export async function completeMfaSetup(code: string): Promise<MfaEnableResponse> {
  const response = await apiClient.post<MfaEnableResponse>('/mfa/setup/complete', { code });
  return response.data;
}

/**
 * Verify a TOTP code
 */
export async function verifyMfaCode(code: string): Promise<MfaVerifyResponse> {
  const response = await apiClient.post<MfaVerifyResponse>('/mfa/verify', { code });
  return response.data;
}

/**
 * Verify a backup code
 */
export async function verifyBackupCode(backupCode: string): Promise<MfaVerifyResponse> {
  const response = await apiClient.post<MfaVerifyResponse>('/mfa/verify/backup', { backupCode });
  return response.data;
}

/**
 * Disable MFA for the authenticated user
 */
export async function disableMfa(code: string): Promise<void> {
  await apiClient.post('/mfa/disable', { code });
}

/**
 * Regenerate backup codes (requires current TOTP code)
 */
export async function regenerateBackupCodes(code: string): Promise<MfaBackupCodesResponse> {
  const response = await apiClient.post<MfaBackupCodesResponse>('/mfa/backup-codes/regenerate', { code });
  return response.data;
}

/**
 * Set recovery email (requires current TOTP code)
 */
export async function setRecoveryEmail(recoveryEmail: string, code: string): Promise<void> {
  await apiClient.post('/mfa/recovery-email', { recoveryEmail, code });
}

/**
 * Complete login with MFA code (called after initial login returns requiresMfa)
 */
export async function loginWithMfa(mfaSessionToken: string, code: string, isBackupCode = false) {
  const response = await apiClient.post('/auth/login/mfa', {
    mfaSessionToken,
    code,
    isBackupCode,
  });
  return response.data;
}

// ==================== Helper Functions ====================

/**
 * Check if a login response indicates MFA is required
 */
export function isMfaRequired(response: unknown): response is MfaRequiredResponse {
  return (
    typeof response === 'object' &&
    response !== null &&
    'requiresMfa' in response &&
    (response as MfaRequiredResponse).requiresMfa === true
  );
}
